/* eslint-disable max-lines */
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';

import {
  Company,
  DashpivotEvent,
  DashpivotModules,
  EventNotifierService,
  EventTypes,
  PlanTypeLimits,
  UserTypes,
} from '@site-mate/dashpivot-shared-library';
import { CompanyPlanTypes } from '@site-mate/sitemate-global-shared';

import { TmpI18NService } from 'app/i18n/tmp-i18n.service';
import { SegmentService } from 'app/segment/segment.service';
import { BannerKeys } from 'app/shared/model/banner-keys.enum';
import { BannerMetadata } from 'app/shared/model/banner-metadata.model';
import { SSOBanner } from 'app/shared/model/sso-banner.model';
import { TeamWeb } from 'app/shared/model/team.model';
import { CompanyService } from 'app/shared/service/company.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { TeamService } from 'app/shared/service/team.service';
import { TimingMarkerService } from 'app/shared/service/timing-marker.service';
import { ToastrService } from 'app/shared/service/toastr.service';
import { AccountUpgradeLimitModalContext } from 'app/user/account-upgrade/account-upgrade-limit/account-upgrade-limit-context.enum';
import { AccountUpgradeLimitService } from 'app/user/account-upgrade/account-upgrade-limit/account-upgrade-limit.service';
import { UserService } from 'app/user/user.service';

import { UserInvite } from '../../user-invite.model';
import { TeamPermissions, UserWeb } from '../../user.model';
import {
  UserEventContexts,
  ToastMessages,
  LearnMoreLinks,
  VisitorNotPermittedMessages,
  VisitorsAllowedInPlan,
} from '../user-folder-helpers';

@Component({
  selector: 'cc-user-team',
  templateUrl: './user-team.component.html',
  styleUrls: ['./user-team.component.scss'],
})
export class UserTeamComponent implements OnInit, OnDestroy {
  readonly bannerKey = BannerKeys.SsoFeatureAvailable;
  readonly bannerStorageKey = BannerMetadata[this.bannerKey].localStorageKey;

  unSubscribe = new Subject<void>();
  teamUsers: UserWeb[];
  teamId: string;
  projectId: string;
  companyId: string;
  currentUserId: string;
  folderUserType: UserTypes;
  currentUserEmail: string;
  canEditProfile: boolean;
  domainsDefined: boolean;
  title: string;
  subtitle: string;
  learnMoreLink: string;
  canInviteOrRemoveTeamController = false;
  isLoading = true;
  domains: Company['domains'] = [];
  jointVentureDomains: Company['jointVentureDomains'] = [];
  teamPermissions = Object.values(TeamPermissions);
  subtitleMessageKey = 'teamUserHeaderSubtitle';
  visitorsPermittedInPlan: boolean;
  domainNotDefinedBanner: boolean;
  visitorsNotAllowedBanner: boolean;
  teamUsersDifferentType: UserWeb[];
  upgradePlanMessage: string;
  ssoFeatureMessage: SSOBanner;
  isLoadingBanner: boolean;
  visitorNotAllowedBannerText: string;

  teamUserModel: UserInvite = {
    email: '',
    working: false,
    submitted: false,
    isExistingUser: false,
    isWorkplaceDomain: false,
    isPublicDomain: false,
    isExistingUserDifferentType: false,
  };

  readonly userTypes = UserTypes;
  readonly userEventContexts = UserEventContexts;
  readonly toastMessages = ToastMessages;
  readonly learnMoreLinks = LearnMoreLinks;

  constructor(
    protected readonly teamService: TeamService,
    protected readonly errorHandler: ErrorHandler,
    protected readonly segmentService: SegmentService,
    protected readonly userService: UserService,
    protected readonly toastrService: ToastrService,
    protected readonly timingMarkerService: TimingMarkerService,
    protected readonly i18nService: TmpI18NService,
    protected readonly companyService: CompanyService,
    private readonly route: ActivatedRoute,
    private readonly accountUpgradeLimitService: AccountUpgradeLimitService,
  ) {}

  ngOnInit(): void {
    this.currentUserId = this.userService.getCurrentUser().id;
    this.currentUserEmail = this.userService.getCurrentUser().email;
    this.upgradePlanMessage = this.i18nService.getMessage('upgradePlanToUnlockFeature');

    this.setUserType();
    this.setUpHeaderData();
    this.checkCredentials();
    this.initializeTeamUsers();
    this.subscribeToDifferentUsersType();
    this.getSSOBannerMessage();
  }

  private setUserType() {
    const { data } = this.route.snapshot;
    this.folderUserType = data.userType;
  }

  private setUpHeaderData() {
    this.title = this.i18nService.getMessage(this.folderUserType);
    this.subtitle = this.i18nService.getMessage('teamUserHeaderSubtitle');
    this.learnMoreLink = this.learnMoreLinks[this.folderUserType];
  }

  checkCredentials() {
    this.userService
      .isTeamControllerOrAbove$()
      .pipe(takeUntil(this.unSubscribe))
      .subscribe((canEdit) => {
        this.canInviteOrRemoveTeamController = canEdit;

        if (this.folderUserType === UserTypes.Visitor) {
          this.setInviteTeamVisitorUserPermissions();
        }
      });

    this.canEditProfile = this.userService.isAdmin();
  }

  ngOnDestroy(): void {
    this.unSubscribe.next();
    this.unSubscribe.complete();
  }

  setInviteTeamVisitorUserPermissions(): void {
    this.teamService.currentTeam
      .pipe(
        filter((team) => team.isTeam && team.id),
        takeUntil(this.unSubscribe),
      )
      .subscribe((team) => {
        const { domains, jointVentureDomains, billing } = team.companyMetadata as Company;
        this.domains = domains || [];
        this.jointVentureDomains = jointVentureDomains || [];
        this.domainsDefined = domains && domains.length > 0;

        this.visitorsPermittedInPlan = VisitorsAllowedInPlan(billing);

        this.domainNotDefinedBanner =
          this.folderUserType === UserTypes.Visitor && !this.domainsDefined && this.visitorsPermittedInPlan;

        this.setTeamInviteVisitorPermission();
        this.setVisitorNotAllowedBannerText(billing.planType);

        this.isLoadingBanner = false;
      });
  }

  private setTeamInviteVisitorPermission() {
    const isVisitor = this.folderUserType === UserTypes.Visitor;

    this.visitorsNotAllowedBanner =
      isVisitor && this.canInviteOrRemoveTeamController && !this.visitorsPermittedInPlan;
  }

  private setVisitorNotAllowedBannerText(planType: CompanyPlanTypes) {
    this.visitorNotAllowedBannerText = VisitorNotPermittedMessages(planType);
  }

  initializeTeamUsers(): void {
    const timingMarker = this.timingMarkerService.start(
      new DashpivotEvent(EventTypes.UserTypeViewed, {
        Context: 'Team Controller',
        Hierarchy: 'team',
      }),
    );
    this.teamService.currentTeam
      .pipe(
        filter((team) => team.isTeam && team.id),
        switchMap((team) => this.setValuesAndGetUser(team, this.folderUserType)),
        takeUntil(this.unSubscribe),
      )
      .subscribe(
        (users) => {
          const teamControllers = this.getTeamControllers(users);
          const teamMembers = this.getTeamMembers(users);
          this.teamUsers = [...teamControllers, ...teamMembers];
          this.isLoading = false;
          this.timingMarkerService.finish(timingMarker);
        },
        (error) => this.errorHandler.handle(error),
      );
  }

  getTeamControllers(users: UserWeb[]): UserWeb[] {
    const teamControllers = users.filter(this.isUserOnlyATeamController.bind(this));
    return teamControllers.map((user) => {
      return { ...user, permission: TeamPermissions.Controller };
    });
  }

  getTeamMembers(users: UserWeb[]): UserWeb[] {
    const teamMembers = users.filter(this.isUserOnlyATeamMember.bind(this));
    return teamMembers.map((user) => {
      return { ...user, permission: TeamPermissions.Member };
    });
  }

  refreshTeamUsers(): void {
    this.teamService
      .getUsers(this.teamId, this.folderUserType)
      .pipe(takeUntil(this.unSubscribe))
      .subscribe(
        (users) => {
          const teamControllers = this.getTeamControllers(users);
          const teamMembers = this.getTeamMembers(users);
          this.teamUsers = [...teamControllers, ...teamMembers];
        },
        (error) => this.errorHandler.handle(error),
      );
  }

  onRemoveTeamUser(user: UserWeb): void {
    this.teamUsers = this.teamUsers?.filter((tempUser) => tempUser.id !== user.id);
    this.teamService
      .removeFromTeam(this.teamId, user.id)
      .pipe(takeUntil(this.unSubscribe))
      .subscribe(
        () => {
          this.trackUserRemovedEvent(user);
          this.toastrService.successByKey(this.toastMessages[this.folderUserType].remove);
        },
        (error) => {
          this.refreshTeamUsers();
          this.errorHandler.handle(error);
        },
      );
  }

  async onInviteTeamMember(): Promise<void> {
    const isExistingTeamMember = this.teamUsers?.some(({ email }) => email === this.teamUserModel.email);

    this.teamUserModel.isExistingUser = isExistingTeamMember;
    this.teamUserModel.isExistingUserDifferentType = this.userHasAnotherFolderType(this.teamUserModel);

    if (this.folderUserType === UserTypes.Visitor) {
      const userValidation = await this.visitorUserValidation();

      if ('error' in userValidation) {
        this.errorHandler.handle(userValidation.error.message);
        return;
      }

      this.teamUserModel.isWorkplaceDomain = userValidation.isWorkplaceDomain;
      this.teamUserModel.isPublicDomain = userValidation.isPublicDomain;
    }

    if (
      !isExistingTeamMember &&
      !this.teamUserModel.isExistingUserDifferentType &&
      !this.teamUserModel.isWorkplaceDomain &&
      !this.teamUserModel.isPublicDomain
    ) {
      this.inviteTeamUser(this.teamUserModel, false, false);
    }
  }

  private async visitorUserValidation() {
    const domains = [...this.domains, ...this.jointVentureDomains];
    return this.userService.visitorUserValidation(domains, this.teamUserModel.email);
  }

  private subscribeToDifferentUsersType() {
    const otherUserType =
      this.folderUserType === UserTypes.Visitor ? UserTypes.Contributor : UserTypes.Visitor;

    this.teamService.currentTeam
      .pipe(
        filter((team) => team.isTeam && team.id),
        switchMap((team) => this.setValuesAndGetUser(team, otherUserType)),
        takeUntil(this.unSubscribe),
      )
      .subscribe(
        (users) => {
          const teamControllers = this.getTeamControllers(users);
          const teamMembers = this.getTeamMembers(users);
          this.teamUsersDifferentType = [...teamControllers, ...teamMembers];
        },
        (error) => this.errorHandler.handle(error),
      );
  }

  private getSSOBannerMessage() {
    this.userService
      .getSSOBannerMessage()
      .pipe(takeUntil(this.unSubscribe))
      .subscribe((message) => {
        this.ssoFeatureMessage = message;
      });
  }

  private userHasAnotherFolderType(user) {
    return this.teamUsersDifferentType?.some(({ email }) => email === user.email);
  }

  inviteTeamUser(model: UserInvite, isTeamController: boolean, permissionUpdate: boolean): void {
    const toastMessage = permissionUpdate
      ? this.toastMessages[this.folderUserType].update
      : this.toastMessages[this.folderUserType].invite;

    model.working = true;

    this.teamService
      .addMember(this.teamId, model.email, {
        isTeamController,
        userType: this.folderUserType,
      })
      .pipe(takeUntil(this.unSubscribe))
      .subscribe(
        () => {
          this.toastrService.successByKey(toastMessage);
          void EventNotifierService.notify(
            new DashpivotEvent(EventTypes.UserInvited, {
              Hierarchy: 'Team',
              Context: this.userEventContexts[this.folderUserType],
            }),
            this.segmentService,
          );
          this.updateCurrentUserPermission(model.email);
          this.resetModel(model);
          this.refreshTeamUsers();
        },
        (error) => {
          model.working = false;
          this.errorHandler.handle(error);
        },
      );
  }

  resetModel(model: UserInvite): void {
    model.email = '';
    model.working = false;
    model.submitted = false;
    model.isExistingUser = false;
    model.isWorkplaceDomain = false;
    model.isPublicDomain = false;
  }

  onUpdatePermission(user: UserWeb) {
    const userInvite: UserInvite = {
      ...this.teamUserModel,
      email: user.email,
    };
    const isTeamController = user.permission === TeamPermissions.Controller;

    this.inviteTeamUser(userInvite, isTeamController, true);
  }

  private updateCurrentUserPermission(email: string) {
    if (email === this.currentUserEmail) {
      this.userService.updateCurrentUser().pipe(takeUntil(this.unSubscribe)).subscribe();
    }
  }

  private isUserOnlyATeamController(user: UserWeb) {
    const { projectId, companyId } = this;

    return (
      user.teamControllerOf?.includes(this.teamId) &&
      !user.projectControllerOf?.includes(projectId) &&
      !user.companyControllerOf?.includes(companyId)
    );
  }

  private isUserOnlyATeamMember(user: UserWeb): boolean {
    const { projectId, companyId } = this;

    return (
      !user.teamControllerOf?.includes(this.teamId) &&
      !user.projectControllerOf?.includes(projectId) &&
      !user.companyControllerOf?.includes(companyId)
    );
  }

  private trackUserRemovedEvent(user: UserWeb): void {
    void EventNotifierService.notify(
      new DashpivotEvent(EventTypes.UserRemoved, {
        Hierarchy: 'Team',
        teamId: this.teamId,
        userId: user.id,
        Context: this.userEventContexts[this.folderUserType],
      }),
      this.segmentService,
    );
  }

  private setValuesAndGetUser(team: TeamWeb, folderUserType: UserTypes): Observable<UserWeb[]> {
    this.teamId = team.id;
    this.projectId = team.projectId;
    this.companyId = team.companyId;

    return this.teamService.getUsers(this.teamId, folderUserType);
  }

  openUpgradeModalVisitorUsers() {
    this.accountUpgradeLimitService.openModal();
  }

  openUpgradeModalSSO() {
    this.accountUpgradeLimitService.openModal(AccountUpgradeLimitModalContext.UnlockSSO);
  }
}
